import React from 'react';

// material-ui
import { alpha, Box, ButtonBase, Stack, Tab, Tabs, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import { themeColors } from 'views/ConstantColors';
import Bank from './bank-names/list';
import BranchCode from './branch-code/list';
import BudgetLine from './budget-line/list';
import Department from './department/list';
import DistrictTable from './district/list';
import JobTitle from './job-title/list';
import ProjectCode from './project-code/list';
import Province from './province/list';
import Site from './site/list';

// assets
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import LocationCityOutlinedIcon from '@mui/icons-material/LocationCityOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';

const WORKSPACE_NAVY = '#213363';

const configurationGroups = [
    {
        label: 'Locations',
        items: [
            {
                label: 'Province',
                description: 'Maintain the provinces available when defining travel locations and service areas.',
                icon: MapOutlinedIcon,
                component: Province
            },
            {
                label: 'District',
                description: 'Manage districts and their province relationships for accurate destination selection.',
                icon: PlaceOutlinedIcon,
                component: DistrictTable
            },
            {
                label: 'Site',
                description: 'Define operational sites and the locations employees can select in travel workflows.',
                icon: LocationCityOutlinedIcon,
                component: Site
            }
        ]
    },
    {
        label: 'Organization',
        items: [
            {
                label: 'Department',
                description: 'Keep the organization structure current for routing, reporting, and approvals.',
                icon: ApartmentOutlinedIcon,
                component: Department
            },
            {
                label: 'Job Title',
                description: 'Maintain official job titles used across employee and approval records.',
                icon: BadgeOutlinedIcon,
                component: JobTitle
            }
        ]
    },
    {
        label: 'Financial setup',
        items: [
            {
                label: 'Branch Code',
                description: 'Manage branch identifiers used to allocate and reconcile travel expenditure.',
                icon: BusinessOutlinedIcon,
                component: BranchCode
            },
            {
                label: 'Project Code',
                description: 'Define project codes available when assigning travel costs and activities.',
                icon: AccountTreeOutlinedIcon,
                component: ProjectCode
            },
            {
                label: 'Budget Line',
                description: 'Configure budget lines used to classify and approve travel expenditure.',
                icon: RequestQuoteOutlinedIcon,
                component: BudgetLine
            },
            {
                label: 'Bank Name',
                description: 'Maintain the approved financial institutions available for payment details.',
                icon: AccountBalanceOutlinedIcon,
                component: Bank
            }
        ]
    }
];

const configurationAreas = configurationGroups.flatMap((group) =>
    group.items.map((item) => ({
        ...item,
        group: group.label
    }))
);

const getTabId = (index) => `configuration-tab-${index}`;
const getPanelId = (index) => `configuration-panel-${index}`;

const Index = () => {
    const theme = useTheme();
    const isCompactNavigation = useMediaQuery(theme.breakpoints.down('md'));
    const [value, setValue] = React.useState(0);
    const activeArea = configurationAreas[value];
    const ActiveComponent = activeArea.component;
    const ActiveIcon = activeArea.icon;

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const handleRailKeyDown = (event, index) => {
        let nextIndex = index;

        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
            nextIndex = (index + 1) % configurationAreas.length;
        } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
            nextIndex = (index - 1 + configurationAreas.length) % configurationAreas.length;
        } else if (event.key === 'Home') {
            nextIndex = 0;
        } else if (event.key === 'End') {
            nextIndex = configurationAreas.length - 1;
        } else {
            return;
        }

        event.preventDefault();
        setValue(nextIndex);
        document.getElementById(getTabId(nextIndex))?.focus();
    };

    return (
        <MainCard
            content={false}
            border
            sx={{
                overflow: 'hidden',
                borderColor: `${themeColors.grey[200]} !important`,
                borderRadius: '10px',
                backgroundColor: '#ffffff',
                boxShadow: '0 8px 24px rgba(15, 23, 42, 0.05)'
            }}
        >
            {isCompactNavigation && (
                <Box
                    component="nav"
                    aria-label="Configuration areas"
                    sx={{
                        px: { xs: 1, sm: 1.5 },
                        pt: 1,
                        backgroundColor: themeColors.grey[50],
                        borderBottom: `1px solid ${themeColors.grey[200]}`
                    }}
                >
                    <Tabs
                        value={value}
                        onChange={handleChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        aria-label="Configuration areas"
                        sx={{
                            minHeight: 58,
                            '& .MuiTabs-indicator': {
                                height: 3,
                                backgroundColor: themeColors.warning.main
                            },
                            '& .MuiTab-root': {
                                minHeight: 58,
                                minWidth: 112,
                                px: 1.5,
                                py: 1,
                                color: themeColors.text.secondary,
                                textTransform: 'none',
                                transition: 'color 160ms ease, background-color 160ms ease',
                                '&:hover': {
                                    color: WORKSPACE_NAVY,
                                    backgroundColor: alpha(WORKSPACE_NAVY, 0.04)
                                },
                                '&.Mui-selected': {
                                    color: WORKSPACE_NAVY,
                                    fontWeight: 700
                                },
                                '&:focus-visible': {
                                    outline: `2px solid ${themeColors.secondary.main}`,
                                    outlineOffset: -2
                                },
                                '@media (prefers-reduced-motion: reduce)': {
                                    transition: 'none'
                                }
                            }
                        }}
                    >
                        {configurationAreas.map((area, index) => {
                            const AreaIcon = area.icon;

                            return (
                                <Tab
                                    key={area.label}
                                    id={getTabId(index)}
                                    aria-controls={getPanelId(index)}
                                    icon={<AreaIcon />}
                                    iconPosition="start"
                                    label={
                                        <Box sx={{ minWidth: 0, textAlign: 'left' }}>
                                            <Typography
                                                component="span"
                                                sx={{ display: 'block', fontSize: '0.8rem', fontWeight: 'inherit' }}
                                            >
                                                {area.label}
                                            </Typography>
                                            <Typography
                                                component="span"
                                                sx={{
                                                    display: 'block',
                                                    color: themeColors.grey[500],
                                                    fontSize: '0.625rem',
                                                    fontWeight: 600,
                                                    lineHeight: 1.2,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.04em'
                                                }}
                                            >
                                                {area.group}
                                            </Typography>
                                        </Box>
                                    }
                                    sx={{
                                        '& .MuiSvgIcon-root': {
                                            mr: 0.8,
                                            mb: '0 !important',
                                            fontSize: 19
                                        }
                                    }}
                                />
                            );
                        })}
                    </Tabs>
                </Box>
            )}

            <Box sx={{ display: { xs: 'block', md: 'grid' }, gridTemplateColumns: '260px minmax(0, 1fr)', minHeight: 560 }}>
                {!isCompactNavigation && (
                    <Box
                        component="nav"
                        aria-label="Configuration areas"
                        role="tablist"
                        aria-orientation="vertical"
                        sx={{
                            p: 2,
                            backgroundColor: themeColors.grey[50],
                            borderRight: `1px solid ${themeColors.grey[200]}`
                        }}
                    >
                        {configurationGroups.map((group, groupIndex) => {
                            const previousItems = configurationGroups
                                .slice(0, groupIndex)
                                .reduce((total, currentGroup) => total + currentGroup.items.length, 0);

                            return (
                                <Box key={group.label} sx={{ mb: groupIndex === configurationGroups.length - 1 ? 0 : 2.25 }}>
                                    <Typography
                                        component="h2"
                                        sx={{
                                            px: 1.25,
                                            mb: 0.75,
                                            color: themeColors.grey[500],
                                            fontSize: '0.6875rem',
                                            fontWeight: 700,
                                            letterSpacing: '0.08em',
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        {group.label}
                                    </Typography>
                                    <Stack spacing={0.5}>
                                        {group.items.map((area, itemIndex) => {
                                            const index = previousItems + itemIndex;
                                            const selected = value === index;
                                            const AreaIcon = area.icon;

                                            return (
                                                <ButtonBase
                                                    key={area.label}
                                                    id={getTabId(index)}
                                                    role="tab"
                                                    aria-selected={selected}
                                                    aria-controls={getPanelId(index)}
                                                    tabIndex={selected ? 0 : -1}
                                                    onClick={() => setValue(index)}
                                                    onKeyDown={(event) => handleRailKeyDown(event, index)}
                                                    sx={{
                                                        position: 'relative',
                                                        width: '100%',
                                                        minHeight: 44,
                                                        justifyContent: 'flex-start',
                                                        px: 1.25,
                                                        py: 0.85,
                                                        overflow: 'hidden',
                                                        borderRadius: '7px',
                                                        color: selected ? WORKSPACE_NAVY : themeColors.text.secondary,
                                                        backgroundColor: selected ? '#ffffff' : 'transparent',
                                                        border: '1px solid',
                                                        borderColor: selected ? themeColors.grey[200] : 'transparent',
                                                        fontWeight: selected ? 700 : 500,
                                                        textAlign: 'left',
                                                        transition:
                                                            'background-color 160ms ease, border-color 160ms ease, color 160ms ease',
                                                        '&::before': {
                                                            content: '""',
                                                            position: 'absolute',
                                                            top: 7,
                                                            bottom: 7,
                                                            left: 0,
                                                            width: 3,
                                                            borderRadius: '0 2px 2px 0',
                                                            backgroundColor: selected ? themeColors.warning.main : 'transparent'
                                                        },
                                                        '&:hover': {
                                                            color: WORKSPACE_NAVY,
                                                            backgroundColor: selected ? '#ffffff' : alpha(WORKSPACE_NAVY, 0.045)
                                                        },
                                                        '&:focus-visible': {
                                                            outline: `2px solid ${themeColors.secondary.main}`,
                                                            outlineOffset: 1
                                                        },
                                                        '@media (prefers-reduced-motion: reduce)': {
                                                            transition: 'none'
                                                        }
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 30,
                                                            height: 30,
                                                            mr: 1.25,
                                                            flexShrink: 0,
                                                            display: 'grid',
                                                            placeItems: 'center',
                                                            borderRadius: '6px',
                                                            color: selected ? '#ffffff' : themeColors.secondary.main,
                                                            backgroundColor: selected
                                                                ? WORKSPACE_NAVY
                                                                : alpha(themeColors.secondary.main, 0.1)
                                                        }}
                                                    >
                                                        <AreaIcon sx={{ fontSize: 18 }} />
                                                    </Box>
                                                    <Typography component="span" sx={{ fontSize: '0.825rem', fontWeight: 'inherit' }}>
                                                        {area.label}
                                                    </Typography>
                                                </ButtonBase>
                                            );
                                        })}
                                    </Stack>
                                </Box>
                            );
                        })}
                    </Box>
                )}

                <Box component="section" sx={{ minWidth: 0, backgroundColor: '#ffffff' }}>
                    <Box
                        sx={{
                            px: { xs: 2, sm: 2.5, lg: 3 },
                            py: { xs: 2, sm: 2.5 },
                            borderBottom: `1px solid ${themeColors.grey[200]}`
                        }}
                    >
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={{ xs: 1.25, sm: 2 }}
                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                            justifyContent="space-between"
                        >
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <Box
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        flexShrink: 0,
                                        display: 'grid',
                                        placeItems: 'center',
                                        borderRadius: '8px',
                                        color: themeColors.secondary.dark,
                                        backgroundColor: themeColors.secondary.lighter,
                                        border: `1px solid ${alpha(themeColors.secondary.main, 0.16)}`
                                    }}
                                >
                                    <ActiveIcon sx={{ fontSize: 22 }} />
                                </Box>
                                <Box>
                                    <Typography
                                        component="h2"
                                        sx={{
                                            color: themeColors.text.primary,
                                            fontSize: { xs: '1rem', sm: '1.125rem' },
                                            fontWeight: 700,
                                            lineHeight: 1.35
                                        }}
                                    >
                                        {activeArea.label}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            mt: 0.25,
                                            maxWidth: 700,
                                            color: themeColors.text.secondary,
                                            fontSize: '0.8rem',
                                            lineHeight: 1.5
                                        }}
                                    >
                                        {activeArea.description}
                                    </Typography>
                                </Box>
                            </Stack>
                            <Stack direction="row" spacing={0.8} alignItems="center" sx={{ pl: { xs: 7, sm: 0 }, flexShrink: 0 }}>
                                <Box
                                    aria-hidden="true"
                                    sx={{ width: 18, height: 2, borderRadius: 1, backgroundColor: themeColors.warning.main }}
                                />
                                <Typography
                                    sx={{
                                        color: themeColors.grey[500],
                                        fontSize: '0.6875rem',
                                        fontWeight: 700,
                                        letterSpacing: '0.06em',
                                        textTransform: 'uppercase'
                                    }}
                                >
                                    {activeArea.group}
                                </Typography>
                            </Stack>
                        </Stack>
                    </Box>

                    <Box
                        id={getPanelId(value)}
                        role="tabpanel"
                        aria-labelledby={getTabId(value)}
                        tabIndex={0}
                        sx={{
                            minWidth: 0,
                            p: { xs: 1.5, sm: 2, lg: 2.5 },
                            '& .configuration-module > .MuiBox-root': {
                                p: '0 !important'
                            },
                            '& .configuration-module > .MuiBox-root > .MuiBox-root:first-of-type': {
                                display: 'inline-flex',
                                maxWidth: '100%',
                                mb: '16px !important',
                                p: '3px',
                                border: `1px solid ${themeColors.grey[200]}`,
                                borderRadius: '8px',
                                backgroundColor: themeColors.grey[100]
                            },
                            '& .configuration-module > .MuiBox-root > .MuiBox-root:first-of-type .MuiTabs-root': {
                                minHeight: 34
                            },
                            '& .configuration-module > .MuiBox-root > .MuiBox-root:first-of-type .MuiTabs-flexContainer': {
                                gap: 0.5
                            },
                            '& .configuration-module > .MuiBox-root > .MuiBox-root:first-of-type .MuiTab-root': {
                                minHeight: 34,
                                minWidth: 0,
                                px: { xs: 1.25, sm: 1.75 },
                                py: 0.6,
                                borderRadius: '6px',
                                color: themeColors.text.secondary,
                                fontSize: '0.78rem !important',
                                fontWeight: 600,
                                '& .MuiSvgIcon-root': {
                                    mb: '0 !important',
                                    mr: 0.75,
                                    fontSize: 17
                                },
                                '&.Mui-selected': {
                                    color: WORKSPACE_NAVY,
                                    backgroundColor: '#ffffff',
                                    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)'
                                }
                            },
                            '& .configuration-module > .MuiBox-root > .MuiBox-root:first-of-type .MuiTabs-indicator': {
                                display: 'none'
                            },
                            '& .configuration-module > .MuiBox-root > .MuiBox-root:nth-of-type(2)': {
                                mt: '0 !important'
                            },
                            '& .MuiTableContainer-root': {
                                maxWidth: '100%',
                                overflowX: 'auto',
                                border: `1px solid ${themeColors.grey[200]}`,
                                borderRadius: '7px',
                                boxShadow: 'none'
                            },
                            '& .MuiTableCell-head': {
                                backgroundColor: WORKSPACE_NAVY
                            },
                            '& .MuiButton-containedPrimary': {
                                backgroundColor: WORKSPACE_NAVY,
                                '&:hover': {
                                    backgroundColor: '#17264d'
                                }
                            },
                            '&:focus-visible': {
                                outline: `2px solid ${themeColors.secondary.main}`,
                                outlineOffset: -2
                            }
                        }}
                    >
                        <Box className="configuration-module">
                            <ActiveComponent />
                        </Box>
                    </Box>
                </Box>
            </Box>
        </MainCard>
    );
};

export default Index;
